import { HTTP_INTERCEPTORS, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
// import { CookieService } from 'ngx-cookie-service';
import { AuthService } from './core/services/authentication.service';
import { RouteUrlConstants } from './models/route-url-constants';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { LoaderService } from './services/loader.service';
import { AppCookieService } from '@ace/ace';

const TOKEN_HEADER_KEY = 'authorization';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  isRefreshTokenSent: boolean;
  isRefreshTokenFetched: boolean;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  
  private authService: AuthService;
  private cookieService: AppCookieService;
  private route: Router;
  private loader: LoaderService;
  private toastr: ToastrService;
  private translate: TranslateService;

  constructor(private injector: Injector) {
    // Use injector to lazily get dependencies and avoid circular dependency
  }

  private getAuthService(): AuthService {
    if (!this.authService) {
      this.authService = this.injector.get(AuthService);
    }
    return this.authService;
  }

  private getCookieService(): AppCookieService {
    if (!this.cookieService) {
      this.cookieService = this.injector.get(AppCookieService);
    }
    return this.cookieService;
  }

  private getRouter(): Router {
    if (!this.route) {
      this.route = this.injector.get(Router);
    }
    return this.route;
  }

  private getLoaderService(): LoaderService {
    if (!this.loader) {
      this.loader = this.injector.get(LoaderService);
    }
    return this.loader;
  }

  private getToastrService(): ToastrService {
    if (!this.toastr) {
      this.toastr = this.injector.get(ToastrService);
    }
    return this.toastr;
  }

  private getTranslateService(): TranslateService {
    if (!this.translate) {
      this.translate = this.injector.get(TranslateService);
    }
    return this.translate;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.getLoaderService().showLoader()
    let authReq = req;
    const skipAuthHeaders = req.headers.get('skipAuthHeaders');
    req = req.clone({ headers: req.headers.delete('skipAuthHeaders') });
    if (!skipAuthHeaders) {
      const token = this.getCookieService().getCookie("accessToken");
      if (token) {
        if (token !== null) {
          authReq = this.addTokenHeader(req, token);
        }
      }
    }

    return this.handleCall(req, next, authReq);
  }

  private handleCall(req: HttpRequest<any>, next: HttpHandler, authReq: HttpRequest<any>): Observable<HttpEvent<any>> {
    return next.handle(authReq).pipe(catchError(error => {
      console.log("ERROR", error);
      console.log("REQUEST", req);
      console.log("AUTH REQ", authReq);
      
      if (error.error.httpStatus === 'UNAUTHORIZED' || (error instanceof HttpErrorResponse && !authReq.url.includes('auth') && error.status === 401)) {
        return this.handle401Error(authReq, next);
      } else if (error.status === 403) {
        this.getRouter().navigateByUrl(RouteUrlConstants.FORBIDDEN);
      } else if (error.status === 500) {
        this.getToastrService().error('Something went wrong .....')
      }
      return throwError(() => error);
    }),
      finalize(() => {
        this.getLoaderService().hideLoader()
      })
    );
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const refreshToken = this.getCookieService().getCookie("refreshToken");
    if (!this.isRefreshTokenFetched) {
      if (refreshToken) {
        this.clearCookie();
        this.isRefreshTokenSent = true;
        this.isRefreshTokenFetched = true;
        return this.getAuthService().refreshToken(refreshToken).pipe(
          take(1),
          switchMap((tokenRes: any) => {
            this.getCookieService().setCookie("accessToken", tokenRes.content.accessToken, { path: '/' });
            this.getCookieService().setCookie("refreshToken", refreshToken, { path: '/' });
            this.isRefreshTokenFetched = false;
            this.refreshTokenSubject.next(tokenRes.content.accessToken);
            return this.handleCall(req, next, this.addTokenHeader(req, tokenRes.content.accessToken));

          }),
          catchError((err) => {
            if (err.error.httpStatus === 'UNAUTHORIZED' || (err instanceof HttpErrorResponse && !req.url.includes('auth') && err.status === 401)) {
              this.isRefreshTokenFetched = false;
              this.clearCookie();
              this.logout();
            }
            return throwError(() => err);
          })
        );
      } else if (!this.isRefreshTokenSent) {
        this.clearCookie();
        this.isRefreshTokenFetched = false;
        this.logout();
        this.getToastrService().error(this.getTranslateService().instant("Your session has expired. Please log in again to continue"));
        return throwError(() => new Error('Token is empty'));
      }
    }
    else {
      // Queue requests while the token is being refreshed
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null), // Wait until the token is available
        take(1),
        switchMap((token) => {
          return this.handleCall(req, next, this.addTokenHeader(req, token));
        }) // Retry the request with the new token
      );
    }

    return throwError(() => new Error('Unhandled error scenario in handle401Error.'));

  }

  private logout() {
    this.getRouter().navigateByUrl('login');
  }
  private clearCookie() {
    this.getCookieService().deleteCookie("accessToken");
    this.getCookieService().deleteCookie("refreshToken");
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    let headerToken: string;
    if (token) {
      if (token.startsWith("Bearer "))
        headerToken = token;
      else
        headerToken = "Bearer " + token;

      return request.clone(
        {
          setHeaders: {
            Authorization: `${headerToken}`
          }
        }
      );
    } else {
      return request.clone({ headers: request.headers.set(TOKEN_HEADER_KEY, "") });
    }
  }
}

export const authInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
];
